import * as React from 'react'
import { useNavigate, useOutletContext } from 'react-router-dom'

import { Alert, Box, Button, List, Spinner, Text } from '@factorialco/deprecated-design-system'

import Modal from 'modules/core/components/Modal'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'

import useAddCompetenciesToRole from 'modules/job_catalog/hooks/useAddCompetenciesToRole'
import useCreateCompetencies from 'modules/job_catalog/hooks/useCreateCompetencies'
import type { Role } from 'modules/job_catalog/hooks/useRole'
import useRoleCompetenciesSuggestions from 'modules/job_catalog/hooks/useRoleCompetenciesSuggestions'

import Item from './Item'

interface OutletContext {
  role: Role
}

const Component: React.FC = () => {
  const {
    company: { id: companyId },
  } = useCurrentSession()
  const { role } = useOutletContext<OutletContext>()
  const { suggestions, isLoading, remove, error, refetch } = useRoleCompetenciesSuggestions(
    role.name,
    companyId
  )
  const { addCompetenciesToRole, isLoading: isAddingCompetenciesToRole } =
    useAddCompetenciesToRole()
  const { createCompetencies, isLoading: isCreatingCompetencies } = useCreateCompetencies()
  const navigate = useNavigate()

  const handleClose = () => {
    track(trackingEvents.jobCatalog.assignAiCompetenciesCancel)
    navigate(-1)
  }

  const handleCreateCompetencies = async () => {
    const nextCompetencies = suggestions.map((x) => ({
      name: x.competencyName,
      description: x.competencyDescription,
    }))
    const createResult = await createCompetencies(nextCompetencies)

    return handleResult(createResult, () => undefined)
  }

  const handleAssignCompetencies = async (competenciesIds: number[]) => {
    const assignResult = await addCompetenciesToRole(role.id, competenciesIds)

    return handleResult(assignResult, () => undefined)
  }

  const handleSubmit = async () => {
    track(trackingEvents.jobCatalog.assignAiCompetenciesSubmit)

    const handledCreate = await handleCreateCompetencies()

    if (!handledCreate.success || !handledCreate.data.competencies) return

    const competenciesIds = handledCreate.data.competencies.map((x) => x.id)
    const handledAssign = await handleAssignCompetencies(competenciesIds)

    if (handledAssign.success) {
      navigate(-1)
    }
  }

  const items = suggestions.map(({ competencyId, competencyName, competencyDescription }) => (
    <Item
      key={competencyName}
      description={competencyDescription}
      name={competencyName}
      onRemove={() => remove(competencyName)}
      id={competencyId}
    />
  ))

  return (
    <Modal
      illustration="template"
      title={i18n.t('job_catalog.competencies.ai_autofill_title')}
      description={i18n.t('job_catalog.competencies.ai_autofill_description')}
      onClose={handleClose}
      onSubmit={handleSubmit}
      footer={
        <>
          <Button onClick={handleClose} type="secondary">
            {i18n.t('common.cancel')}
          </Button>
          {error ? (
            <Button onClick={refetch}>{i18n.t('common.try_again')}</Button>
          ) : (
            <Button disabled={isLoading} submit>
              {i18n.t('job_catalog.competencies.ai_autofill_submit')}
            </Button>
          )}
        </>
      }
    >
      <Box gap="s24">
        {error && <Alert color="error">{i18n.t('errors.generic_error')}</Alert>}
        {(isAddingCompetenciesToRole || isCreatingCompetencies) && (
          <Box alignItems="center" paddingY="s120">
            <Spinner />
          </Box>
        )}
        {isLoading && (
          <Box alignItems="center" paddingY="s120">
            <Spinner />
            <Text.Normal weight="medium">
              {i18n.t('job_catalog.competencies.ai_autofill_spinner_title')}
            </Text.Normal>
            <Text.Normal>
              {i18n.t('job_catalog.competencies.ai_autofill_spinner_description')}
            </Text.Normal>
          </Box>
        )}
        {!isLoading &&
          !isAddingCompetenciesToRole &&
          !isCreatingCompetencies &&
          !!items?.length && (
            <>
              <List>{items}</List>
              <Alert>{i18n.t('job_catalog.competencies.ai_autofill_alert')}</Alert>
            </>
          )}
      </Box>
    </Modal>
  )
}

export default Component
